import { useCallback, useEffect, useRef, useState } from "react";
import { MAX_SERVER_RECORDING_MS, fileNameFor, pickRecordingMime } from "../lib/serverStt";
import { buildAlternatives } from "../lib/speechAlternatives";

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? "";

type VoiceState = "idle" | "starting" | "recording" | "processing";

/** "browser" uses the Web Speech API (the default).
 *  "server" records a clip with MediaRecorder and POSTs it to the backend's
 *  /voice, which forwards it to the configured provider (decisions/0015). */
export type VoiceMode = "browser" | "server";

/** What server mode needs from the page. */
export interface ServerModeOptions {
  /** Sent with the clip so the provider gets the session's recent names as hints. */
  sessionId?: string | null;
  /** The backend answered 503 with `fallback`: record this question again with the browser engine. */
  onFallback?: () => void;
  /** The clip was refused or lost (too large, wrong type, network): ask the user to try again. */
  onMiss?: () => void;
}

interface ISpeechRecognitionEvent {
  readonly resultIndex: number;
  readonly results: {
    readonly length: number;
    [index: number]: {
      readonly isFinal: boolean;
      readonly length: number;
      readonly [index: number]: { readonly transcript: string };
    };
  };
}
interface ISpeechRecognition {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  maxAlternatives: number;
  onresult: ((e: ISpeechRecognitionEvent) => void) | null;
  onerror: ((e?: { error?: string; message?: string }) => void) | null;
  onend: (() => void) | null;
  onaudiostart?: (() => void) | null;
  start(): void;
  stop(): void;
  abort?: () => void;
}

// If audiostart never comes, switch to "recording" anyway so the UI can't stick on "starting".
const AUDIO_START_FALLBACK_MS = 3_000;

/** Abort a recogniser so iOS gives the microphone back, ignoring "already ended". */
function release(rec: ISpeechRecognition | null): void {
  if (!rec?.abort) return;
  try {
    rec.abort();
  } catch {
    /* already ended */
  }
}
type SpeechRecognitionCtor = new () => ISpeechRecognition;

declare global {
  interface Window {
    SpeechRecognition?: SpeechRecognitionCtor;
    webkitSpeechRecognition?: SpeechRecognitionCtor;
  }
}

// Safety cut-off — stop automatically after 60 s so the mic never stays open forever
const MAX_RECORDING_MS = 60_000;

/**
 * useVoiceInput — STT abstraction supporting two modes:
 *
 *   "browser" (default): uses Web Speech API — near-real-time, no backend.
 *   "server":            records via MediaRecorder, POSTs the clip to /voice,
 *                        then emits the transcript the provider returned.
 *
 * Returns:
 *   startRecording()  — begin listening
 *   stopRecording()   — stop and emit the full accumulated transcript
 *   voiceState        — "idle" | "starting" (waiting for iOS to capture audio) | "recording" | "processing"
 *   isSupported       — false when the required API is unavailable
 */
export function useVoiceInput(
  /** `alternatives`: other whole-question readings, best first (browser mode
   *  only; empty when the recogniser offers none).  `engine` is "browser" or
   *  the provider name the backend reported. */
  onTranscript: (text: string, alternatives: string[], engine: string) => void,
  mode: VoiceMode = "browser",
  options: ServerModeOptions = {},
) {
  const [voiceState, setVoiceState] = useState<VoiceState>("idle");
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Read at call time so the page can pass a fresh object each render.
  const optionsRef = useRef(options);
  useEffect(() => { optionsRef.current = options; }, [options]);

  // --- Browser Web Speech API refs ---
  const recognitionRef = useRef<ISpeechRecognition | null>(null);
  // One entry per final segment: that segment's alternatives, best first.
  const segmentsRef    = useRef<string[][]>([]);

  // --- Server-mode MediaRecorder refs ---
  const mediaStreamRef   = useRef<MediaStream | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef        = useRef<Blob[]>([]);

  // Feature detection
  const SR =
    typeof window !== "undefined"
      ? (window.SpeechRecognition ?? window.webkitSpeechRecognition)
      : undefined;

  const hasMediaRecorder =
    typeof window !== "undefined" &&
    typeof MediaRecorder !== "undefined" &&
    !!navigator.mediaDevices?.getUserMedia;

  const isSupported = mode === "server" ? hasMediaRecorder : !!SR;

  // -------------------------------------------------------------------------
  // Shared cleanup
  // -------------------------------------------------------------------------
  // Cleanup runs after each recording.  We DO release the MediaStream
  // tracks here even though it can occasionally cause iOS Safari to
  // re-prompt for the microphone — keeping the stream alive forces iOS
  // into a "play-and-record" audio session, which ducks (lowers) the
  // volume of subsequent TTS playback.  Clean audio playback is more
  // important than a one-time permission prompt, especially for the
  // voice-quality story the app is built around.
  const _cleanup = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    // Browser
    recognitionRef.current = null;
    segmentsRef.current    = [];
    // Server mode — fully release the mic so iOS exits "record" audio mode and
    // returns to "playback" mode at full volume.
    mediaStreamRef.current?.getTracks().forEach((t) => t.stop());
    mediaStreamRef.current   = null;
    mediaRecorderRef.current = null;
    chunksRef.current        = [];
  }, []);

  // -------------------------------------------------------------------------
  // Browser — Web Speech API
  // -------------------------------------------------------------------------
  // Browser recording as verified on the owner's iPhone (2026-10-26,
  // docs/voice-input-learnings.md §2.O):
  //   * "starting" until the recogniser reports audio capture — iOS starts
  //     listening 0.3–2 s after the tap, and words spoken before were lost;
  //   * interim results kept, because iOS often answers stop() with
  //     "aborted" and no final result;
  //   * the recogniser aborted after every recording (and before a new one),
  //     so iOS releases the microphone.
  const startRecordingBrowser = useCallback(async () => {
    if (voiceState !== "idle" || !SR) return;
    release(recognitionRef.current);
    segmentsRef.current = [];

    const rec = new SR();
    rec.lang            = "en-US";
    rec.continuous      = true;   // keep listening through pauses
    rec.interimResults  = true;
    rec.maxAlternatives = 5;      // n-best readings for the corrector (decisions/0006)
    recognitionRef.current = rec;
    let latestInterim = "";

    let live = false;
    const goLive = () => {
      if (live || recognitionRef.current !== rec) return;
      live = true;
      setVoiceState((s) => (s === "starting" ? "recording" : s));
    };
    rec.onaudiostart = goLive;
    setTimeout(goLive, AUDIO_START_FALLBACK_MS);

    rec.onresult = (e) => {
      let interim = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const result = e.results[i];
        if (result.isFinal) {
          const alts: string[] = [];
          for (let j = 0; j < result.length; j++) alts.push(result[j]?.transcript?.trim() ?? "");
          segmentsRef.current.push(alts);
        } else {
          interim += ` ${result[0]?.transcript?.trim() ?? ""}`;
        }
      }
      // A finalised phrase replaces the interim words it came from, so they
      // aren't sent twice; what's still interim is the unfinished tail.
      latestInterim = interim.trim();
    };

    // An error and the end event both finish the recording, exactly once.
    let finished = false;
    const finish = () => {
      if (finished) return;
      finished = true;
      release(rec);
      const segments = segmentsRef.current;
      const finals = segments.map((alts) => alts[0] ?? "").filter(Boolean).join(" ").trim();
      // iOS aborted before finalising: the interim words are all we have.
      const transcript = [finals, latestInterim].filter(Boolean).join(" ").trim();
      const alternatives = buildAlternatives(segments);
      _cleanup();
      if (transcript) {
        setVoiceState("processing");
        onTranscript(transcript, alternatives, "browser");
      } else {
        setVoiceState("idle");
      }
    };
    rec.onerror = finish;
    rec.onend = finish;

    try {
      rec.start();
      setVoiceState("starting");
      timeoutRef.current = setTimeout(() => recognitionRef.current?.stop(), MAX_RECORDING_MS);
    } catch (err) {
      console.error("SpeechRecognition start failed:", err);
      _cleanup();
      setVoiceState("idle");
    }
  }, [voiceState, SR, onTranscript, _cleanup]);

  const stopRecordingBrowser = useCallback(() => {
    if (recognitionRef.current && (voiceState === "recording" || voiceState === "starting")) {
      recognitionRef.current.stop(); // triggers onend → onTranscript
    }
  }, [voiceState]);

  // -------------------------------------------------------------------------
  // Server — MediaRecorder → POST /voice → the provider's transcript
  // -------------------------------------------------------------------------
  // The clip is capped at 15 s and posted as recorded (audio/mp4 on iOS,
  // webm or ogg elsewhere); the backend never decodes it.  The microphone
  // is released before the upload starts, so nothing plays while it is held
  // (the one iOS rule left, docs/voice-input-learnings.md §2.O).
  const sendClip = useCallback(async (blob: Blob) => {
    const { sessionId, onFallback, onMiss } = optionsRef.current;
    try {
      const formData = new FormData();
      formData.append("audio", blob, fileNameFor(blob.type));
      if (sessionId) formData.append("session_id", sessionId);
      const resp = await fetch(`${BASE_URL}/voice`, { method: "POST", body: formData });
      if (resp.status === 503) {
        const body = (await resp.json().catch(() => ({}))) as { fallback?: string };
        if (body.fallback) { onFallback?.(); return; }
      }
      if (!resp.ok) { onMiss?.(); return; }
      const { transcript, engine } = (await resp.json()) as { transcript?: string; engine?: string };
      if (transcript?.trim()) onTranscript(transcript.trim(), [], engine || "server");
    } catch (err) {
      console.error("Server transcription failed:", err);
      onMiss?.();
    } finally {
      setVoiceState("idle");
    }
  }, [onTranscript]);

  const startRecordingServer = useCallback(async () => {
    if (voiceState !== "idle") return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;

      const mimeType = pickRecordingMime(MediaRecorder.isTypeSupported?.bind(MediaRecorder));
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      mediaRecorderRef.current = recorder;
      chunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: recorder.mimeType || mimeType || "audio/webm" });
        _cleanup(); // the mic is free before the upload begins
        setVoiceState("processing");
        void sendClip(blob);
      };

      recorder.start();
      setVoiceState("recording");
      timeoutRef.current = setTimeout(() => {
        if (mediaRecorderRef.current?.state === "recording") {
          mediaRecorderRef.current.stop();
        }
      }, MAX_SERVER_RECORDING_MS);
    } catch (err) {
      console.error("MediaRecorder start failed:", err);
      _cleanup();
      setVoiceState("idle");
    }
  }, [voiceState, sendClip, _cleanup]);

  const stopRecordingServer = useCallback(() => {
    if (timeoutRef.current) { clearTimeout(timeoutRef.current); timeoutRef.current = null; }
    if (mediaRecorderRef.current?.state === "recording") {
      mediaRecorderRef.current.stop(); // triggers onstop → sendClip → onTranscript
    }
  }, []);

  // -------------------------------------------------------------------------
  // Public API — route to the active mode
  // -------------------------------------------------------------------------
  const startRecording = mode === "server" ? startRecordingServer : startRecordingBrowser;
  const stopRecording  = mode === "server" ? stopRecordingServer  : stopRecordingBrowser;

  const resetState = useCallback(() => {
    _cleanup();
    setVoiceState("idle");
  }, [_cleanup]);

  return { startRecording, stopRecording, resetState, voiceState, isSupported };
}
